// Native (Capacitor Android) Google sign-in via Emergent-managed auth.
// Google blocks OAuth inside WebViews, so the flow runs in the system browser
// (Chrome Custom Tabs) and returns to the app through the wassla:// deep link
// carrying a one-time session_id, which the backend exchanges for the app JWT.
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { App } from '@capacitor/app';
import { getApiBase } from '../api/httpClient';

export const isNativeApp = () => Capacitor.isNativePlatform();

// Persistent "OAuth round-trip in progress" flag. Set BEFORE the system
// browser opens so the app's very first frame after returning through
// wassla://oauth (warm resume OR cold start) is the signing-in state —
// the welcome/login page must never be visible during an active callback.
const OAUTH_PENDING_KEY = 'wassla_oauth_pending';

export function setOAuthPending(on) {
  try {
    if (on) localStorage.setItem(OAUTH_PENDING_KEY, '1');
    else localStorage.removeItem(OAUTH_PENDING_KEY);
  } catch { /* no storage */ }
}

export function isOAuthReturnPending() {
  try { return localStorage.getItem(OAUTH_PENDING_KEY) === '1'; } catch { return false; }
}

// Opens Wassla's own Google OAuth flow (backend-driven) in the system browser.
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export async function startNativeGoogleLogin() {
  const bridgeUrl = `${getApiBase()}/api/auth/native-bridge`;
  const authUrl = `${getApiBase()}/api/auth/google/start?redirect=${encodeURIComponent(bridgeUrl)}`;
  await Browser.open({ url: authUrl });
}

export async function exchangeSessionId(sessionId) {
  const res = await fetch(`${getApiBase()}/api/auth/oauth/callback`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ session_id: sessionId }),
  });
  if (!res.ok) {
    const err = new Error(`oauth_exchange_${res.status}`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

// Registers the deep-link listener (native only). onSessionId receives the
// one-time session_id whenever the app is opened via wassla://oauth?session_id=...
export function registerNativeAuthListener(onSessionId) {
  if (!isNativeApp()) return () => {};
  const p = App.addListener('appUrlOpen', async ({ url }) => {
    const m = String(url || '').match(/session_id=([^&#]+)/);
    if (!m) return;
    // Hand the session over FIRST (sets the exchanging state synchronously),
    // THEN close the browser — so the browserFinished cancel-detector below
    // can never race ahead and clear the signing-in overlay on success.
    onSessionId(decodeURIComponent(m[1]));
    try { await Browser.close(); } catch { /* browser may already be closed */ }
  });
  return () => { p.then((h) => h.remove()).catch(() => {}); };
}

// Cold start: if Android killed the app while the browser was open, the
// wassla://oauth return relaunches the app and appUrlOpen may never fire —
// the deep link is only available via getLaunchUrl().
export async function getLaunchSessionId() {
  if (!isNativeApp()) return null;
  try {
    const launch = await App.getLaunchUrl();
    const m = String(launch?.url || '').match(/session_id=([^&#]+)/);
    return m ? decodeURIComponent(m[1]) : null;
  } catch { return null; }
}

// Fires when the Chrome Custom Tab closes. On a successful sign-in the
// session handler runs first (see registerNativeAuthListener ordering), so a
// browserFinished with no exchange in flight deterministically means the user
// cancelled — no timers involved.
export function registerBrowserFinishedListener(onFinished) {
  if (!isNativeApp()) return () => {};
  const p = Browser.addListener('browserFinished', () => onFinished());
  return () => { p.then((h) => h.remove()).catch(() => {}); };
}
